<template>
  <PuppyCollapseItem
    title="部分边线参数"
    name="partial-edge"
    checkbox
    :check-value="enabled"
    :checkbox-disabled="!editable"
    @update:check-value="(value) => emit('update:enabled', value)"
  >
    <div class="flex flex-col gap-[10px]" data-testid="partial-edge-parameters-panel">
      <div
        v-for="partialEdge in partialEdges"
        :key="partialEdge.itemRef"
        class="flex flex-col gap-2 p-2 border border-white/12 rounded-[6px]"
      >
        <div class="text-xs font-600 opacity-82">{{ resolveItemLabel(partialEdge.itemRef) }}</div>

        <PuppyFormItem>
          <template #icon>
            <span class="inline-flex items-center justify-center min-w-[28px] text-xs leading-none">开始</span>
          </template>
          <n-select
            size="small"
            :value="partialEdge.startCondition"
            :options="partialConditionOptions"
            :disabled="!editable"
            @update:value="(value) => handleConditionChange(partialEdge.itemRef, 'start', value)"
          />
        </PuppyFormItem>

        <PuppyFormItem v-if="showPartialValue(partialEdge.startCondition)">
          <template #icon>
            <span class="inline-flex items-center justify-center min-w-[28px] text-xs leading-none">开始值</span>
          </template>
          <div class="flex items-center gap-2 w-full">
            <n-input-number
              class="flex-1 min-w-0"
              size="small"
              show-button
              :value="formatPartialValueForPropertyManager(partialEdge.startCondition, partialEdge.startValue ?? 0)"
              :disabled="!editable"
              :min="0"
              :precision="3"
              :step="1"
              @update:value="(value) => handleValueCommit(partialEdge.itemRef, 'start', value)"
            />
            <span class="shrink-0 text-xs opacity-68">{{ resolveValueUnit(partialEdge.startCondition) }}</span>
          </div>
        </PuppyFormItem>

        <PuppyFormItem v-if="partialEdge.startCondition === 'reference-offset'">
          <template #icon>
            <span class="inline-flex items-center justify-center min-w-[28px] text-xs leading-none">参考</span>
          </template>
          <PuppyInputSelect
            :ref="(component) => setReferenceInputRef(partialEdge.itemRef, 'start', component)"
            :items="resolveReferenceItems(partialEdge, 'start')"
            :active="isReferenceActive(partialEdge.itemRef, 'start')"
            :visible-rows="1"
            item-key-field="persistentRef"
            item-label-field="label"
            empty-text="选择参考"
            :disabled="!editable"
            @update:active="(active) => emit('reference-active-change', partialEdge.itemRef, 'start', active)"
            @delete-all="emit('reference-delete', partialEdge.itemRef, 'start')"
          />
        </PuppyFormItem>

        <PuppyFormItem>
          <template #icon>
            <span class="inline-flex items-center justify-center min-w-[28px] text-xs leading-none">终止</span>
          </template>
          <n-select
            size="small"
            :value="partialEdge.endCondition"
            :options="partialConditionOptions"
            :disabled="!editable"
            @update:value="(value) => handleConditionChange(partialEdge.itemRef, 'end', value)"
          />
        </PuppyFormItem>

        <PuppyFormItem v-if="showPartialValue(partialEdge.endCondition)">
          <template #icon>
            <span class="inline-flex items-center justify-center min-w-[28px] text-xs leading-none">终止值</span>
          </template>
          <div class="flex items-center gap-2 w-full">
            <n-input-number
              class="flex-1 min-w-0"
              size="small"
              show-button
              :value="formatPartialValueForPropertyManager(partialEdge.endCondition, partialEdge.endValue ?? 0)"
              :disabled="!editable"
              :min="0"
              :precision="3"
              :step="1"
              @update:value="(value) => handleValueCommit(partialEdge.itemRef, 'end', value)"
            />
            <span class="shrink-0 text-xs opacity-68">{{ resolveValueUnit(partialEdge.endCondition) }}</span>
          </div>
        </PuppyFormItem>

        <PuppyFormItem v-if="partialEdge.endCondition === 'reference-offset'">
          <template #icon>
            <span class="inline-flex items-center justify-center min-w-[28px] text-xs leading-none">参考</span>
          </template>
          <PuppyInputSelect
            :ref="(component) => setReferenceInputRef(partialEdge.itemRef, 'end', component)"
            :items="resolveReferenceItems(partialEdge, 'end')"
            :active="isReferenceActive(partialEdge.itemRef, 'end')"
            :visible-rows="1"
            item-key-field="persistentRef"
            item-label-field="label"
            empty-text="选择参考"
            :disabled="!editable"
            @update:active="(active) => emit('reference-active-change', partialEdge.itemRef, 'end', active)"
            @delete-all="emit('reference-delete', partialEdge.itemRef, 'end')"
          />
        </PuppyFormItem>
      </div>
    </div>
  </PuppyCollapseItem>
</template>

<script setup lang="ts">
import { computed, nextTick, watch } from 'vue';
import { NInputNumber, NSelect } from 'naive-ui';
import type {
  BlendPointRef,
  BlendPartialCondition,
  PartialEdgeParam,
} from '@puppycad/cad-core';
import {
  convertMToMm,
  convertMmToM,
} from '@puppycad/cad-core';
import type { BlendPartialReferenceTarget } from '@/core/features/blend/partial-edge';
import { isSamePartialReferenceTarget } from '@/core/features/blend/partial-edge';
import PuppyCollapseItem from '@/components/PuppyCollapseItem/PuppyCollapseItem.vue';
import PuppyFormItem from '@/components/PuppyFormItem/PuppyFormItem.vue';
import PuppyInputSelect from '@/components/PuppyInputSelect/PuppyInputSelect.vue';

type PartialReferenceSide = 'start' | 'end';

interface PuppyInputSelectExposed {
  /** 将浏览器焦点放到输入框根节点。 */
  focus?: () => void;
  /** 从输入框根节点移除浏览器焦点。 */
  blur?: () => void;
}

interface PartialEdgeParametersSectionProps {
  /** 是否启用部分边线参数。 */
  enabled: boolean;
  /** 当前字段是否允许编辑。 */
  editable: boolean;
  /** 部分边线参数集合。 */
  partialEdges: PartialEdgeParam[];
  /** 当前主选择项，用于显示边线标签。 */
  selectedItems: Array<{ persistentRef: string; label: string }>;
  /** 当前参考选择目标。 */
  activeReferenceTarget: BlendPartialReferenceTarget | null;
}

const props = withDefaults(defineProps<PartialEdgeParametersSectionProps>(), {
  editable: true,
});

const emit = defineEmits<{
  /** 同步启用状态。 */
  (event: 'update:enabled', value: boolean): void;
  /** 修改起止条件。 */
  (event: 'condition-change', itemRef: string, side: 'start' | 'end', condition: BlendPartialCondition): void;
  /** 修改起止值。 */
  (event: 'value-commit', itemRef: string, side: 'start' | 'end', value: number): void;
  /** 激活或取消参考选择输入。 */
  (event: 'reference-active-change', itemRef: string, side: 'start' | 'end', active: boolean): void;
  /** 清空参考选择。 */
  (event: 'reference-delete', itemRef: string, side: 'start' | 'end'): void;
}>();

const partialConditionOptions = [
  { label: '无', value: 'none' },
  { label: '距离等距', value: 'offset' },
  { label: '百分比等距', value: 'percentage-offset' },
  { label: '参考等距', value: 'reference-offset' },
];

const selectedItemByRef = computed(() => new Map(
  props.selectedItems.map((item) => [item.persistentRef, item]),
));

const referenceInputRefs = new Map<string, PuppyInputSelectExposed>();

/**
 * 构建部分边线参考输入框的稳定 key。
 *
 * @param itemRef 边引用。
 * @param side 起止侧。
 * @returns 输入框 key。
 */
function buildReferenceInputKey(itemRef: string, side: PartialReferenceSide): string {
  return `${itemRef}:${side}`;
}

/**
 * 记录或释放参考输入框组件实例。
 *
 * @param itemRef 边引用。
 * @param side 起止侧。
 * @param component 组件实例。
 */
function setReferenceInputRef(
  itemRef: string,
  side: PartialReferenceSide,
  component: unknown,
): void {
  const key = buildReferenceInputKey(itemRef, side);
  if (!component) {
    referenceInputRefs.delete(key);
    return;
  }
  referenceInputRefs.set(key, component as PuppyInputSelectExposed);
}

/**
 * 聚焦当前激活的参考输入框。
 *
 * 设计说明：
 * - “参考等距”切换后输入框会通过 v-if 延迟挂载；
 * - 因此 watch 中先等待 nextTick，再通过组件暴露的 focus 方法聚焦；
 * - 业务选择目标仍由父级 activeReferenceTarget 控制，组件只负责 UI 焦点。
 *
 * @param target 当前参考目标。
 */
function focusActiveReferenceInput(target: BlendPartialReferenceTarget | null): void {
  if (!target) {
    return;
  }
  referenceInputRefs.get(buildReferenceInputKey(target.itemRef, target.side))?.focus?.();
}

watch(
  () => props.activeReferenceTarget,
  async (target) => {
    await nextTick();
    focusActiveReferenceInput(target);
  },
  { flush: 'post', immediate: true },
);

/**
 * 处理起止条件变更。
 *
 * @param itemRef 边引用。
 * @param side 起止侧。
 * @param value 条件值。
 */
function handleConditionChange(
  itemRef: string,
  side: 'start' | 'end',
  value: string | null,
): void {
  if (
    value === 'none'
    || value === 'offset'
    || value === 'percentage-offset'
    || value === 'reference-offset'
  ) {
    emit('condition-change', itemRef, side, value);
  }
}

/**
 * 处理起止值提交。
 *
 * @param itemRef 边引用。
 * @param side 起止侧。
 * @param payload 输入载荷。
 */
function handleValueCommit(
  itemRef: string,
  side: 'start' | 'end',
  value: number | null,
): void {
  const partialEdge = props.partialEdges.find((item) => item.itemRef === itemRef);
  const condition = side === 'start'
    ? partialEdge?.startCondition
    : partialEdge?.endCondition;
  emit('value-commit', itemRef, side, normalizePartialValueFromPropertyManager(condition ?? 'offset', value));
}

/**
 * 判断条件是否需要数值。
 *
 * @param condition 条件。
 * @returns 是否展示数值输入。
 */
function showPartialValue(condition: BlendPartialCondition): boolean {
  return condition === 'offset'
    || condition === 'percentage-offset';
}

//------ 部分边线长度单位转换 开始------
/**
 * 将部分边线底层数值转换为 PropertyManager 显示值。
 *
 * @param condition 当前条件。
 * @param value 底层数值。
 * @returns UI 显示数值。
 */
function formatPartialValueForPropertyManager(condition: BlendPartialCondition, value: number): number {
  if (condition === 'percentage-offset') {
    return value;
  }

  // 距离等距在 UI 显示毫米，底层仍保存米制偏移。
  return Number(convertMToMm(value).toFixed(3));
}

/**
 * 将部分边线输入值转换为底层数值。
 *
 * @param condition 当前条件。
 * @param value UI 输入值。
 * @returns 底层数值。
 */
function normalizePartialValueFromPropertyManager(condition: BlendPartialCondition, value: number | null): number {
  const normalizedValue = Math.max(0, value ?? 0);
  if (condition === 'percentage-offset') {
    return normalizedValue;
  }

  // 用户输入 90 表示 90mm，传给底层前转换成 0.09m。
  return convertMmToM(normalizedValue);
}
//------ 部分边线长度单位转换 结束------

/**
 * 解析数值单位。
 *
 * @param condition 条件。
 * @returns 单位文本。
 */
function resolveValueUnit(condition: BlendPartialCondition): string {
  return condition === 'percentage-offset' ? '%' : 'mm';
}

/**
 * 解析边线标签。
 *
 * @param itemRef 边引用。
 * @returns 显示标签。
 */
function resolveItemLabel(itemRef: string): string {
  return selectedItemByRef.value.get(itemRef)?.label ?? itemRef;
}

/**
 * 判断参考输入是否激活。
 *
 * @param itemRef 边引用。
 * @param side 起止侧。
 * @returns 是否激活。
 */
function isReferenceActive(itemRef: string, side: 'start' | 'end'): boolean {
  return isSamePartialReferenceTarget(props.activeReferenceTarget, itemRef, side);
}

/**
 * 解析参考项显示列表。
 *
 * @param partialEdge 部分边线参数。
 * @param side 起止侧。
 * @returns 参考显示项集合。
 */
function resolveReferenceItems(
  partialEdge: PartialEdgeParam,
  side: 'start' | 'end',
): Array<{ persistentRef: string; label: string }> {
  const referencePoint = side === 'start'
    ? partialEdge.startReferencePoint
    : partialEdge.endReferencePoint;
  return referencePoint ? [buildReferenceDisplayItem(referencePoint)] : [];
}

/**
 * 构建参考显示项。
 *
 * @param referenceItem 参考选择项。
 * @returns 显示项。
 */
function buildReferenceDisplayItem(
  referencePoint: BlendPointRef,
): { persistentRef: string; label: string } {
  const persistentRef = referencePoint.persistentRef
    ?? referencePoint.subEntityId
    ?? `${referencePoint.bodyObjectId}:${referencePoint.point.join(',')}`;
  return {
    persistentRef,
    label: referencePoint.displayLabel ?? `点<${persistentRef}>`,
  };
}
</script>
